import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import BasePage from "../components/BasePage";

import SidebarTabs, { type TabKey } from "../components/dashboard/SidebarTabs";
import DateFilters from "../components/dashboard/DateFilters";
import ChartSection from "../components/dashboard/ChartSection";
import TableSection from "../components/dashboard/TableSection";
import BarChartSection from "../components/dashboard/BarChartSection";
import ExplorerSection from "../components/dashboard/ExplorerSection";
import PieChartSection from "../components/dashboard/PieChartSection";
import ExportButton from "../components/dashboard/ExportButton";
import transformStatsForExport from "../utils/transformStatsForExport";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";

import Hero from "../components/Hero";

export const API_BASE =
  "https://site--ucwaves-p1-backend-deployment--r9qw5p2frw6c.code.run";

interface DailyActivityItem {
  date: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

interface TopBurnerRaw {
  userAddress: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

interface UserTx {
  date: string;
  NAUBurn?: number;
  NAUYMint?: number;
  NAUNMint?: number;
  [key: string]: unknown;
}

interface Stats {
  dailyActivity: DailyActivityItem[];
  users: string[];
  topBurners: TopBurnerRaw[];
}

function getCurrentWeekRange(): { start: string; end: string } {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const toISOStringDate = (d: Date) => d.toISOString().split("T")[0];
  return {
    start: toISOStringDate(monday),
    end: toISOStringDate(sunday),
  };
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getCurrentWeekRange());
  const [activeTab, setActiveTab] = useState<TabKey>("chart");
  const [selectedUser, setSelectedUser] = useState("");
  const [userTxs, setUserTxs] = useState<UserTx[]>([]);
  const [loadingUserTxs, setLoadingUserTxs] = useState(false);

  const handleTabChange = (tab: TabKey) => setActiveTab(tab);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("❌ Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserTxs = useCallback(async () => {
    if (!selectedUser) return;
    setLoadingUserTxs(true);
    try {
      const res = await axios.get(
        `${API_BASE}/stats/transactions/${selectedUser}`
      );

      setUserTxs(res.data?.transactions || []);
      console.log("✅ User transactions:", res.data);
      if (res.data?.transactions?.length === 0) {
        console.warn("⚠️ No transactions found for user:", selectedUser);
      }
    } catch (error) {
      console.error("❌ Error fetching user txs:", error);
      setUserTxs([]);
    } finally {
      setLoadingUserTxs(false);
    }
  }, [selectedUser]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUserTxs();
  }, [fetchUserTxs]);

  const filteredData =
    stats?.dailyActivity.filter((item) => {
      const itemDate = new Date(item.date);
      return (
        (!dateRange.start || itemDate >= new Date(dateRange.start)) &&
        (!dateRange.end || itemDate <= new Date(dateRange.end))
      );
    }) || [];

  const filteredUserTxs = userTxs.filter((item) => {
    const txDate = item.date.slice(0, 10);
    return (
      (!dateRange.start || txDate >= dateRange.start) &&
      (!dateRange.end || txDate <= dateRange.end)
    );
  });

  const exportCompatibleData = filteredData.map((item) => ({
    date: item.date,
    NAU: item.NAUBurn,
    NAUY: item.NAUYMint,
    NAUN: item.NAUNMint,
  }));

  const transformedData = transformStatsForExport(exportCompatibleData);

  console.log("🧪 filteredData:", filteredData);
  console.log("📦 transformedData:", transformedData);

  return (
    <BasePage
      title="UC Waves Dashboard"
      description="Burn/mint stats visualization from MongoDB"
    >
      <Hero />
      <main className="min-h-screen w-full font-sans px-4 py-16 mt-12">
        <div className="flex items-center justify-around flex-wrap gap-8">
          <img
            src={imgL}
            alt="Left decoration"
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
          <h1 className="text-6xl font-bolder text-secondary-light text-center shadow-red-700">
            UnCensored Waves <br /> Dashboard
          </h1>
          <img
            src={imgR}
            alt="Right decoration"
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          <SidebarTabs activeTab={activeTab} setActiveTab={handleTabChange} />

          <section className="flex-1 w-full flex flex-col items-center">
            {loading && (
              <div className="mb-8 text-blue-700 animate-pulse">
                ⏳ Loading data...
              </div>
            )}
            {!loading && !stats && (
              <div className="mb-8 text-red-600">⚠️ Failed to load data.</div>
            )}

            <DateFilters dateRange={dateRange} setDateRange={setDateRange} />

            {activeTab === "chart" &&
              (transformedData.length > 0 ? (
                <ChartSection data={transformedData} />
              ) : (
                <p className="text-gray-500">No chart data available.</p>
              ))}

            {activeTab === "table" && (
              <TableSection data={transformedData} onRefresh={fetchStats} />
            )}

            {(activeTab === "pie" || activeTab === "distribution") &&
              (transformedData.length > 0 ? (
                <PieChartSection data={transformedData} />
              ) : (
                <p className="text-gray-500 italic mb-4">
                  No distribution data available.
                </p>
              ))}

            {activeTab === "bar" &&
              (stats?.topBurners?.length ? (
                <BarChartSection data={stats.topBurners} />
              ) : (
                <p className="text-gray-500">No top burners data found.</p>
              ))}

            {activeTab === "explorer" && (
              <ExplorerSection
                users={stats?.users || []}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                data={filteredUserTxs}
                loading={loadingUserTxs}
              />
            )}

            {activeTab === "export" && <ExportButton data={transformedData} />}
          </section>
        </div>
      </main>
    </BasePage>
  );
};

export default DashboardPage;
